import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ExpandableListRowProps = {
  primary: ReactNode;
  secondary?: ReactNode;
  status?: ReactNode;
  value?: ReactNode;
  actions?: ReactNode;
  details: ReactNode;
  className?: string;
  defaultExpanded?: boolean;
  controlLayout?: "row" | "column";
  spreadColumnControls?: boolean;
  onRowClick?: () => void;
};

export function ExpandableListRow({
  primary,
  secondary,
  status,
  value,
  actions,
  details,
  className,
  defaultExpanded = false,
  controlLayout = "row",
  spreadColumnControls = false,
  onRowClick,
}: ExpandableListRowProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className={cn("rounded-lg border bg-card", className)}>
      <div
        className={cn("flex items-start gap-3 p-4", onRowClick ? "cursor-pointer" : undefined)}
        onClick={onRowClick}
      >
        <div className="min-w-0 flex-1 space-y-1">
          <div className="min-w-0 text-sm font-semibold text-foreground">{primary}</div>
          {secondary ? (
            <div className="min-w-0 text-xs text-muted-foreground">{secondary}</div>
          ) : null}
        </div>
        {controlLayout === "column" ? (
          <div className="flex items-start gap-2 shrink-0">
            {(value || status) ? (
              <div className="text-right space-y-1">
                {status ? <div className="flex justify-end">{status}</div> : null}
                {value ? <div className="text-sm font-semibold text-foreground">{value}</div> : null}
              </div>
            ) : null}
            <div
              className={cn(
                "flex flex-col items-center gap-0.5 shrink-0",
                spreadColumnControls ? "self-stretch justify-between" : undefined,
              )}
              onClick={(event) => event.stopPropagation()}
            >
              {actions ? <div className="flex h-7 w-7 items-center justify-center">{actions}</div> : null}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="group h-7 w-7 shrink-0 rounded-full bg-transparent hover:bg-transparent items-end pb-0.5"
                onClick={(event) => {
                  event.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
                aria-expanded={expanded}
                aria-label={expanded ? "Hide details" : "Show details"}
              >
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform group-hover:text-foreground",
                    expanded ? "rotate-180" : "rotate-0",
                  )}
                />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 shrink-0">
            {(value || status) ? (
              <div className="text-right space-y-1">
                {value ? <div className="text-sm font-semibold text-foreground">{value}</div> : null}
                {status ? <div className="flex justify-end">{status}</div> : null}
              </div>
            ) : null}
            {actions ? <div className="shrink-0" onClick={(event) => event.stopPropagation()}>{actions}</div> : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((prev) => !prev);
              }}
              aria-expanded={expanded}
              aria-label={expanded ? "Hide details" : "Show details"}
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  expanded ? "rotate-180" : "rotate-0",
                )}
              />
            </Button>
          </div>
        )}
      </div>
      <div
        className={cn(
          "grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0">
          <div
            className={cn(
              "overflow-hidden transition-all duration-200 ease-out",
              expanded ? "border-t px-4 py-3 opacity-100" : "px-4 py-0 opacity-0",
            )}
          >
            {details}
          </div>
        </div>
      </div>
    </div>
  );
}
